import { useEffect, useState } from 'preact/hooks';
import type { ArenaSocket } from '../realtime.js';
import { getSoundSettings, playChatSound, playSound } from '../audio/sounds.js';
import { lobbyChatVisible } from '../components/LobbyChat.js';
import { messagesViewVisible } from '../views/MessagesView.js';
import type {
  ChallengeEndReason,
  ChallengeView,
  ArenaClosureView,
  ChatMessage,
  GameEndedView,
  LobbyPlayer,
  MeState,
  MultiGameEndedView,
  PrivateMessage,
  TableEndReason,
  TableView,
} from '../../shared/protocol.js';

/** User interaction counts as activity; sent at most this often (server resets the idle timer). */
const ACTIVITY_THROTTLE_MS = 30_000;

export interface ChallengeNotice {
  key: number;
  challenge: ChallengeView;
  reason: ChallengeEndReason;
}

export interface ArenaState {
  me: MeState | null;
  players: LobbyPlayer[];
  notices: ChallengeNotice[];
  dismissNotice: (key: number) => void;
  /** Incoming private messages shown as toasts outside the lobby chat. */
  pmNotices: { key: number; from: string; preview: string }[];
  dismissPmNotice: (key: number) => void;
  gameResult: GameEndedView | null;
  /** Arena closure (maintenance), null until the server has told. */
  closure: ArenaClosureView | null;
  clearGameResult: () => void;
  /** Open game tables (multiplayer games, decision 2026-09-27). */
  tables: TableView[];
  /** Chat of the table the user is waiting at. */
  tableChat: ChatMessage[];
  /** Why the user's table closed (other than the game starting). */
  tableClosed: TableEndReason | null;
  clearTableClosed: () => void;
  multiResult: MultiGameEndedView | null;
  clearMultiResult: () => void;
}

export function useArena(socket: ArenaSocket | null, userId: number): ArenaState {
  const [me, setMe] = useState<MeState | null>(null);
  const [players, setPlayers] = useState<LobbyPlayer[]>([]);
  const [notices, setNotices] = useState<ChallengeNotice[]>([]);
  const [gameResult, setGameResult] = useState<GameEndedView | null>(null);
  const [closure, setClosure] = useState<ArenaClosureView | null>(null);
  const [pmNotices, setPmNotices] = useState<{ key: number; from: string; preview: string }[]>([]);
  const [tables, setTables] = useState<TableView[]>([]);
  const [tableChat, setTableChat] = useState<ChatMessage[]>([]);
  const [tableClosed, setTableClosed] = useState<TableEndReason | null>(null);
  const [multiResult, setMultiResult] = useState<MultiGameEndedView | null>(null);

  useEffect(() => {
    if (!socket) return;
    let noticeKey = 0;

    let incomingIds = new Set<string>();
    let tableId: string | null = null;
    let seated = 0;
    socket.on('me:state', (m) => {
      // A new incoming challenge plays the challenge sound.
      if (m.incoming.some((c) => !incomingIds.has(c.id))) playSound('challenge');
      incomingIds = new Set(m.incoming.map((c) => c.id));
      // Another player sits down at my table.
      if (m.table && m.table.id === tableId && m.table.seats.length > seated) playSound('tableJoin');
      if ((m.table?.id ?? null) !== tableId) setTableChat([]);
      tableId = m.table?.id ?? null;
      seated = m.table?.seats.length ?? 0;
      setMe(m);
    });
    socket.on('tables:list', setTables);
    const onTableChat = (p: { tableId: string; message: ChatMessage }) => {
      setTableChat((list) => (list.some((x) => x.id === p.message.id) ? list : [...list, p.message]));
      if (p.message.userId !== userId) playChatSound('chatMessage');
    };
    socket.on('table:chat', onTableChat);
    socket.on('table:closed', ({ reason }) => {
      if (reason !== 'started') setTableClosed(reason);
    });
    socket.on('multi:ended', setMultiResult);
    socket.on('lobby:players', setPlayers);
    const onPm = (m: PrivateMessage) => {
      // Outside the lobby chat (e.g. while playing): a notification with the start of the
      // message and a sound, unless the player has turned these off (user request 2026-09-27).
      if (m.from.id === userId || lobbyChatVisible || messagesViewVisible || !getSoundSettings().pmNotifications) return;
      playChatSound('chatPrivate');
      const key = ++noticeKey;
      const preview = m.text.length > 40 ? `${m.text.slice(0, 40)}…` : m.text;
      setPmNotices((list) => [...list, { key, from: m.from.displayName, preview }]);
      setTimeout(() => setPmNotices((list) => list.filter((n) => n.key !== key)), 8000);
    };
    socket.on('pm:message', onPm);
    socket.on('game:ended', setGameResult);
    socket.on('arena:closure', setClosure);
    socket.on('challenge:ended', ({ challenge, reason }) => {
      if (challenge.from.id === userId && reason === 'accepted') playSound('accepted');
      if (challenge.from.id === userId && reason === 'declined') playSound('declined');
      // The accepting player does not need a notice; the game view opens.
      if (reason === 'accepted' && challenge.to.id === userId) return;
      // A declined challenge is only interesting for the challenger.
      if (reason === 'declined' && challenge.to.id === userId) return;
      const key = ++noticeKey;
      setNotices((list) => [...list, { key, challenge, reason }]);
      setTimeout(() => setNotices((list) => list.filter((n) => n.key !== key)), 6000);
    });

    let lastSent = 0;
    const onActivity = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastSent < ACTIVITY_THROTTLE_MS) return;
      lastSent = now;
      socket.emit('activity');
    };
    const events = ['pointerdown', 'keydown', 'touchstart', 'visibilitychange'] as const;
    events.forEach((e) => addEventListener(e, onActivity, { passive: true }));

    return () => {
      socket.off('me:state');
      socket.off('lobby:players');
      socket.off('pm:message', onPm);
      socket.off('game:ended');
      socket.off('arena:closure');
      socket.off('tables:list');
      socket.off('table:chat', onTableChat);
      socket.off('table:closed');
      socket.off('multi:ended');
      socket.off('challenge:ended');
      events.forEach((e) => removeEventListener(e, onActivity));
    };
  }, [socket, userId]);

  return {
    me,
    players,
    notices,
    dismissNotice: (key) => setNotices((list) => list.filter((n) => n.key !== key)),
    pmNotices,
    dismissPmNotice: (key) => setPmNotices((list) => list.filter((n) => n.key !== key)),
    gameResult,
    closure,
    clearGameResult: () => setGameResult(null),
    tables,
    tableChat,
    tableClosed,
    clearTableClosed: () => setTableClosed(null),
    multiResult,
    clearMultiResult: () => setMultiResult(null),
  };
}

/** Current server time, refreshed at the given interval. */
export function useServerNow(clockOffset: number, intervalMs = 250): number {
  const [now, setNow] = useState(Date.now() + clockOffset);
  useEffect(() => {
    setNow(Date.now() + clockOffset);
    const timer = setInterval(() => setNow(Date.now() + clockOffset), intervalMs);
    return () => clearInterval(timer);
  }, [clockOffset, intervalMs]);
  return now;
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
